import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
// import proj4 from "proj4";
import * as Cesium from "cesium";

import LayerBox from './LayerBox';
import NewLayerBox from './NewLayerBox';
import CloudControlBox from './CloudControlBox';
import RainControlBox from './RainControlBox';
import WindControlBox from './WindControlBox';
import GPUWindControlBox from './GPUWindControlBox';

import CustomControlLineIcon from "../icons/line.svg";
import CustomControlPolygonIcon from "../icons/polyline.svg";
import CustomControlZoomIn from "../icons/zoom-in.svg";
import CustomControlZoomOut from "../icons/zoom-out.svg";
import CustomControlGlobe from "../icons/globe.svg";
import CustomControlLayers from "../icons/layers.svg";
import CustomControlCloudsIcon from "../icons/clouds.svg";
import CustomControlRainIcon from "../icons/rain.svg";
import CustomControlWindIcon from "../icons/wind.svg";
import CustomControlGPUWindIcon from "../icons/gpu-wind.svg";
import CustomControlRainDropIcon from "../icons/raindrop.svg";
import CustomControlBuildingIcon from "../icons/building.svg";
import CustomControlHelicopterIcon from "../icons/helicopter.svg";
import CustomControlLocationPinIcon from "../icons/locationpin.svg";

import { mercatorToLonLat } from '../utils/MercatorToLonLat';
import { initializeClouds, updateCloudParameters, flyToCloudView } from '../utils/VolumetricClouds';
import { initializeRainEffect, updateRainEffect, cleanupRainEffect } from '../utils/RainEffect';
import { initialiseWind, updateWindParameters, flyToWindView } from '../utils/WindVisualization';
import { initialiseGPUWind, flyToGPUWindView } from '../utils/GPUWindVisualization';

import LayerTimestampSelector from './LayerTimestampSelector';

// Debug flag - set to true to enable verbose logging
const DEBUG_KML_LOADING = false;

const CesiumControls = forwardRef(({
  viewerRef,
  showLayerBox,
  setShowLayerBox,
  setShowBubble,
  setShowFolder,
  //loadProject
  layerList,
  layerDate,
  setLayerProgress,
  setFormattedLayers,
  //LayerBox
  formattedLayers,
  layerProgress,
  //TimestampLayerBox
  showTimestampLayerBox,
  setShowTimestampLayerBox,
  timestampLayers,
  timestampLayerSources,
  setTimestampLayerSources,
  timestampLayerVisibility,
  setTimestampLayerVisibility,
  currentTimestampLayerIndex,
  stopTimestampAnimation,
  riverWatermaskLayers,
  riverWatermaskLayerSources,
  setRiverWatermaskLayerSources,
  riverWatermaskLayerVisibility,
  setRiverWatermaskLayerVisibility,
  //RoutesLayerBox
  showRoutesLayerBox,
  setShowRoutesLayerBox,
  routesLayers,
  routesLayerSources,
  setRoutesLayerSources,
  routesLayerVisibility,
  setRoutesLayerVisibility,
  //Helicopter evac routes Layer Box
  showHelicopterRoutesLayerBox,
  setShowHelicopterRoutesLayerBox,
  helicopterRoutesLayers,
  helicopterRoutesLayerSources,
  setHelicopterRoutesLayerSources,
  helicopterRoutesLayerVisibility,
  setHelicopterRoutesLayerVisibility,
  currentHelicopterRoutesLayerIndex,
  //CloudControlBox
  showCloudsBox,
  setShowCloudsBox,
  cloudsEnabled,
  setCloudsEnabled,
  cloudOpacity,
  setCloudOpacity,
  cloudThreshold,
  setCloudThreshold,
  cloudRange,
  setCloudRange,
  cloudSteps,
  setCloudSteps,
  //RainControlBox
  showRainBox,
  setShowRainBox,
  rainEnabled,
  setRainEnabled,
  rainIntensity,
  setRainIntensity,
  rainSpeed,
  setRainSpeed,
  rainBlur,
  setRainBlur,
  //WindControlBox
  showWindBox,
  setShowWindBox,
  windEnabled,
  setWindEnabled,
  windSpeed,
  setWindSpeed,
  windDirection,
  setWindDirection,
  windParticleDensity,
  setWindParticleDensity,
  windAltitude,
  setWindAltitude,
  windTurbulence,
  setWindTurbulence,
  //GPUWindControlBox
  showGpuWindBox,
  setShowGpuWindBox,
  gpuWindEnabled,
  setGpuWindEnabled,
  gpuWindSpeed,
  setGpuWindSpeed,
  gpuWindDirection,
  setGpuWindDirection,
  gpuWindParticleCount,
  setGpuWindParticleCount,
  gpuWindAltitude,
  setGpuWindAltitude,
  gpuWindTurbulence,
  setGpuWindTurbulence,
  gpuWindParticleLifetime,
  setGpuWindParticleLifetime,
  gpuWindParticleSize,
  setGpuWindParticleSize,
  //
  showFloodTimestamps,
  setShowFloodTimestamps,
  showHelicopterEvacRoutes,
  setShowHelicopterEvacRoutes,
  showTallBuildings,
  setShowTallBuildings,
  //Walkthrough
  currentWalkthroughStep,
}, ref) => {

  const [layerSources, setLayerSources] = useState({});
  const [cloudsPrimitive, setCloudsPrimitive] = useState(null);
  const [cloudsUniforms, setCloudsUniforms] = useState(null);
  const [cloudsCenterPoint, setCloudsCenterPoint] = useState(null);
  const [rainStage, setRainStage] = useState(null);
  const [rainUniforms, setRainUniforms] = useState(null);
  const [windPrimitive, setWindPrimitive] = useState(null);
  const [windData, setWindData] = useState(null);
  const [windCenterPoint, setWindCenterPoint] = useState(null);
  const [gpuWindPrimitive, setGpuWindPrimitive] = useState(null);
  const [gpuWindCenterPoint, setGpuWindCenterPoint] = useState(null);

  const [showLayerTimestampSelector, setShowLayerTimestampSelector] = useState(false);
  const [selectedTimestamp, setSelectedTimestamp] = useState(timestampLayers && timestampLayers.length > 0 ? timestampLayers[0].ts : null);
  const [selectedLayerType, setSelectedLayerType] = useState("Flood");

  /* Commented out items from App.js */

  /*export const flyToLocation = (longitude, latitude, height) => {
    if (viewerRef.current && viewerRef.current.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      viewer.camera.flyTo({
        destination: Cartesian3.fromDegrees(longitude, latitude, height),
        duration: 2
      });
    }
  };

  export const setZoomLevel = (zoomLevel) => {
    if (viewerRef.current && viewerRef.current.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      viewer.camera.zoomTo(viewer.camera.positionCartographic.height / zoomLevel);
    }
  };

  const handleZoomChange = () => {
    if (viewerRef.current && viewerRef.current.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      const zoomLevel = viewer.camera.positionCartographic.height;
      console.log(`Current zoom level: ${zoomLevel}`);
    }
  };

  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (viewer) {
      const handleWheel = () => handleZoomChange();
      viewer.container.addEventListener('wheel', handleWheel);

      return () => {
        viewer.container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [viewerRef]);*/

  const [zoomInInterval, setZoomInInterval] = useState(null);
  const [zoomOutInterval, setZoomOutInterval] = useState(null);

  const handleZoomInStart = () => {
    if (!zoomInInterval && viewerRef?.current?.cesiumElement) {
      viewerRef.current.cesiumElement.camera.zoomIn();
      const interval = setInterval(() => {
        viewerRef.current.cesiumElement.camera.zoomIn();
      }, 100);
      setZoomInInterval(interval);
    }
  };

  const handleZoomInEnd = () => {
    if (zoomInInterval) {
      clearInterval(zoomInInterval);
      setZoomInInterval(null);
    }
  };

  const handleZoomOutStart = () => {
    if (!zoomOutInterval && viewerRef?.current?.cesiumElement) {
      viewerRef.current.cesiumElement.camera.zoomOut();
      const interval = setInterval(() => {
        viewerRef.current.cesiumElement.camera.zoomOut();
      }, 100);
      setZoomOutInterval(interval);
    }
  };

  const handleZoomOutEnd = () => {
    if (zoomOutInterval) {
      clearInterval(zoomOutInterval);
      setZoomOutInterval(null);
    }
  };

  const loadHardcodedTiles = async () => {
    // Legend of values for "place":
    // 0 (default) == Stefano Flood 3600
    // 1 == Most of Europe
    // 2 == Romania
    // 3 == Small bit of Romaina, 
    const place = 0;
    var place_dir = "DFO_2117_From_20030101_to_200301080000000000-0000014848"
    var min_x = 9274;
    var max_x = 9467;
    var min_y = 9950;
    var max_y = 11115;
    const mid_x = Math.round((max_x + min_x) / 2);
    const mid_y = Math.round((max_y + min_y) / 2);
    min_x = mid_x - 5;
    max_x = mid_x + 5;
    min_y = mid_y - 5;
    max_y = mid_y + 5;
    var view_west = null;
    var view_south = null;
    var view_east = null;
    var view_north = null;
    switch (place) {
      case 1:
        var place_dir = "DFO_2117_From_20030101_to_200301080000000000-0000000000"
        min_x = 7757;
        max_x = 9274;
        min_y = 9950;
        max_y = 11115;
        break;
      case 2:
        var place_dir = "DFO_2117_From_20030101_to_200301080000000000-0000014848"
        min_x = 9274;
        max_x = 9467;
        min_y = 9950;
        max_y = 11115;
        break;
      case 3:
        var place_dir = "DFO_2117_From_20030101_to_200301080000000000-0000014848"
        var min_x = 9274;
        var max_x = 9467;
        var min_y = 9950;
        var max_y = 11115;
        const mid_x = Math.round((max_x + min_x) / 2);
        const mid_y = Math.round((max_y + min_y) / 2);
        min_x = mid_x - 5;
        max_x = mid_x + 5;
        min_y = mid_y - 5;
        max_y = mid_y + 5;
        break;
      default:
        var place_dir = "WD_RAIN104431@all_painted/WD_RAIN104431@3600_painted"
        var min_x = 8445;
        var max_x = 8449;
        var min_y = 10870;
        var max_y = 10873;
        const extent_x_min = 619106.2451016440754756;
        const extent_x_max = 630394.2451016440754756;
        const extent_y_min = 6551378.9224481238052249;
        const extent_y_max = 6558193.9224481238052249;
        var [view_west, view_south] = mercatorToLonLat(extent_x_min, extent_y_min);
        var [view_east, view_north] = mercatorToLonLat(extent_x_max, extent_y_max);
      // This method would probably be better, 
      // but then I'd have to add proj4 as a dep.
      // proj4.defs("EPSG:3857", "+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs");
      // var [view_west, view_south] = proj4("EPSG:3857", "EPSG:4326", [extent_x_min, extent_y_min]);
      // var [view_east, view_north] = proj4("EPSG:3857", "EPSG:4326", [extent_x_max, extent_y_max]);
    }
    // const zoom_level = 14;

    const viewer = viewerRef.current.cesiumElement;
    console.debug(viewerRef.current);
    console.debug(viewerRef.current?.cesiumElement);
    if (!viewer) {
      console.error("Viewer reference not found!");
      return;
    }

    let files = [];
    for (let i = min_x; i <= max_x; i++) {
      for (let j = min_y; j <= max_y; j++) {
        files.push(`${i}/${j}.kml`);
      }
    }
    console.info(`Loading ${files.length} KML tiles...`);

    var lastKmlDataSource = null;
    for (const file of files) {
      const url = `http://localhost:3001/${place_dir}/14/${file}`;
      console.debug("Loading:", url);
      try {
        const kmlDataSource = await Cesium.KmlDataSource.load(url, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas,
          clampToGround: true
        });
        console.debug(`Loaded KML: ${file}`);
        viewer.dataSources.add(kmlDataSource);
        lastKmlDataSource = kmlDataSource;
      } catch (error) {
        console.error("Error loading KML:", file, error);
      }
    }
    if (view_west && view_south && view_east && view_north) {
      viewer.camera.flyTo({
        destination: Cesium.Rectangle.fromDegrees(
          view_west,
          view_south,
          view_east,
          view_north,
        ),
      });  // TODO: Set the zoom to 14 also?
    } else {
      await viewer.zoomTo(lastKmlDataSource);
    }
    console.debug("Finished loading all KMLs.");
  }

  const loadProject = async () => {
    const viewer = viewerRef.current.cesiumElement;
    viewer.camera.flyTo({
      destination: Cesium.Rectangle.fromDegrees(152.87738274405348, -27.67824234628433, 153.34037444148868, -27.22697287641346),
    });

    const layers = layerList;

    const totalFiles = Object.values(layers).reduce((acc, layer) => {
      const numFiles = (layer.max_x - layer.min_x + 1) * (layer.max_y - layer.min_y + 1);
      return acc + numFiles;
    }, 0);

    let newLayerSources = {};
    let loadedFiles = 0;

    for (const layer of layers) {
      let files = [];
      newLayerSources[layer.name] = [];

      for (let i = layer.min_x; i <= layer.max_x; i++) {
        for (let j = layer.min_y; j <= layer.max_y; j++) {
          files.push(`${i}/${j}.kml`);
        }
      }

      for (const file of files) {
        // const dataSource = await Cesium.KmlDataSource.load(`http://echodemo.com/artefacts/${layer.name}/14/${file}`, {
        const dataSource = await Cesium.KmlDataSource.load(`http://localhost:3001/14/${file}`, {
          camera: viewerRef.current.cesiumElement.scene.camera,
          canvas: viewerRef.current.cesiumElement.scene.canvas,
          clampToGround: true
        });
        newLayerSources[layer.name].push(dataSource);
        await viewerRef.current.cesiumElement.dataSources.add(dataSource);
        loadedFiles++;
        setLayerProgress(Math.round((loadedFiles / totalFiles) * 100));
      }
    }

    setLayerSources(newLayerSources);
  };

  useEffect(() => {
    const regex = /^WD_(.+?)_(\d+m)_(\d+m{1,2})_(\d+h)$/;

    const newLayers = layerList.map(layer => {
      console.log(layer.name);
      const match = layer.name.match(regex);
      if (match) {
        const [_, city, depth, rainfall, time] = match;
        return {
          city: city.replace(/_/g, ' '),
          date: layerDate,
          depth,
          rainfall,
          time,
          checked: false
        };
      }
      return null;
    }).filter(Boolean);

    console.log("Initialized layers:", newLayers);
    setFormattedLayers(newLayers);
  }, []);


  const [useOSMBuildings, setUseOSMBuildings] = useState(true);
  const [showOSMBuildings, setShowOSMBuildings] = useState(false);
  const [OSMBuildingsTileset, setOSMBuildingsTileset] = useState(null);
  // Simple style: show all buildings, color those over 3 stories (10m)
  const OSMBuildingOnConditions = [
    ["${feature['cesium#estimatedHeight']} >= 50", "color('green')"],
    ["${feature['cesium#estimatedHeight']} >= 30", "color('yellow')"],
    ["${feature['cesium#estimatedHeight']} >= 20", "color('orange')"],
    ["${feature['cesium#estimatedHeight']} >= 10", "color('red')"],
    ["true", "color('white', 0.5)"]
  ];
  // Simple style: make everything transparent
  const OSMBuildingOffConditions = [
    ["true", "color('white', 0.0)"]
  ];
  useEffect(() => {
    const initialShowOSMBuildings = async (tileset) => {
      if (tileset && viewerRef.current && viewerRef.current.cesiumElement) {
        const viewer = viewerRef.current.cesiumElement;
        if (viewer) {
          // console.debug("viewer:", viewer);
          viewer.scene.primitives.add(tileset);
        }
      }
    }
    const loadOSMBuildings = async () => {
      const osmBuildings = await Cesium.createOsmBuildingsAsync();
      const conditions = showOSMBuildings ? OSMBuildingOnConditions : OSMBuildingOffConditions;
      osmBuildings.style = new Cesium.Cesium3DTileStyle({
        color: {
          conditions: conditions
        }
      });
      setOSMBuildingsTileset(osmBuildings);
      if (useOSMBuildings) {
        initialShowOSMBuildings(osmBuildings);
      }
    }
    loadOSMBuildings();
  }, []);

  async function toggleOSMBuildingVisibility() {
    // console.debug("Toggling buildings...");
    const newVisibility = !showOSMBuildings;
    let tileset = OSMBuildingsTileset;

    if (newVisibility) {
      tileset.style = new Cesium.Cesium3DTileStyle({
        color: {
          conditions: OSMBuildingOnConditions
        }
      });
      setOSMBuildingsTileset(tileset);
      // console.debug("Adding buildings...");
    } else {
      tileset.style = new Cesium.Cesium3DTileStyle({
        color: {
          conditions: OSMBuildingOffConditions
        }
      });
      setOSMBuildingsTileset(tileset);
      // console.debug("Removing buildings...");
    }
    setShowOSMBuildings(newVisibility);
  }

  // Expose toggleOSMBuildingVisibility to parent via ref
  useImperativeHandle(ref, () => ({
    toggleOSMBuildingVisibility
  }));

  async function ensureLayersAtEnd(layerVisibility, layerSources) {
    const viewer = viewerRef.current.cesiumElement;
    for (const layerName in layerVisibility) {
      if (layerVisibility[layerName]) {
        layerSources[layerName].forEach(dataSource => {
          viewer.dataSources.remove(dataSource);
          viewer.dataSources.add(dataSource);
        });
      }
    }
  }

  async function toggleLayerVisibility(ts, layerName, layerVisibility, layerSources, setLayerVisibility) {
    const viewer = viewerRef.current.cesiumElement;
    // console.log(viewer.dataSources);
    const newVisibility = !layerVisibility[layerName];

    if (layerSources[layerName]) {
      layerSources[layerName].forEach(dataSource => {
        if (newVisibility) {
          viewer.dataSources.add(dataSource);
        } else {
          viewer.dataSources.remove(dataSource);
        }
      });
    } else {
      console.debug(`Layer ${layerName} not found in layerSources...`)
      console.debug("Layer source:", layerSources);
    }
    setLayerVisibility((prev) => ({ ...prev, [ts]: newVisibility }));
  }

  const toggleTimestampLayerVisibility = async (ts, layerName) => {
    await toggleLayerVisibility(ts, layerName, timestampLayerVisibility, timestampLayerSources, setTimestampLayerVisibility);
    await ensureLayersAtEnd(routesLayerVisibility, routesLayerSources);
  }

  const toggleRoutesLayerVisibility = async (ts, layerName) => {
    await toggleLayerVisibility(ts, layerName, routesLayerVisibility, routesLayerSources, setRoutesLayerVisibility);
  }

  // Keep Cesium viewer in sync when timestampLayerVisibility is changed programmatically
  useEffect(() => {
    const applyVisibility = () => {
      try {
        const viewer = viewerRef.current?.cesiumElement;
        if (!viewer) return;

        // Check if viewer is destroyed
        if (viewer.isDestroyed && viewer.isDestroyed()) {
          console.warn('Viewer is destroyed, skipping visibility update');
          return;
        }

        // if (!showTimestampLayerBox) {
        //   for (const layer of timestampLayers) {
        //     const loaded = timestampLayerSources[layer.name];
        //     if (!loaded || !Array.isArray(loaded)) continue;

        //     for (const ds of loaded) {
        //       try {
        //         const exists = viewer.dataSources.contains(ds);
        //         if (exists) viewer.dataSources.remove(ds);
        //       } catch (e) {
        //         console.warn('Error toggling dataSource visibility', e);
        //       }
        //     }
        //   }
        // }

        // Validate dataSources collection is in good state
        if (!viewer.dataSources || typeof viewer.dataSources.contains !== 'function') {
          console.warn('DataSources collection is not ready, skipping visibility update');
          return;
        }

        for (const layer of timestampLayers) {
          const ts = layer.ts;
          const loaded = timestampLayerSources[layer.name];

          // More robust array check with length validation
          if (!loaded || !Array.isArray(loaded) || loaded.length === 0) continue;

          // Create defensive copy to prevent mid-iteration modifications
          const dataSourcesCopy = [...loaded];

          const shouldShow = !!timestampLayerVisibility[ts];
          for (const ds of dataSourcesCopy) {
            // Skip if dataSource is undefined or null
            if (!ds) {
              console.warn('Skipping undefined dataSource for timestamp', ts);
              continue;
            }

            // Skip if dataSource is destroyed or in invalid state
            if (ds.isDestroyed && ds.isDestroyed()) {
              console.warn('Skipping destroyed dataSource for timestamp', ts);
              continue;
            }

            // Check for required properties to ensure dataSource is valid
            if (!ds.entities || !ds.name) {
              console.warn('Skipping invalid dataSource (missing entities or name) for timestamp', ts);
              continue;
            }

            try {
              // Re-validate dataSources collection before each operation
              if (!viewer.dataSources || typeof viewer.dataSources.contains !== 'function') {
                console.warn('DataSources collection became invalid during iteration');
                break;
              }

              const exists = viewer.dataSources.contains(ds);

              if (shouldShow && !exists) {
                // Validate collection before adding
                if (typeof viewer.dataSources.add === 'function') {
                  viewer.dataSources.add(ds);
                }
              } else if (!shouldShow && exists) {
                // Extra safety for remove operation with collection validation
                try {
                  if (typeof viewer.dataSources.remove === 'function') {
                    viewer.dataSources.remove(ds);
                  }
                } catch (removeError) {
                  console.warn('Failed to remove dataSource, may already be removed:', removeError);
                }
              }
            } catch (e) {
              console.warn('Error toggling dataSource visibility', e);
            }
          }
        }
      } catch (error) {
        // Catch any unexpected errors to prevent Cesium render loop from breaking
        console.warn('Error in applyVisibility:', error);
      }
    };

    // Use requestAnimationFrame to defer until after current render cycle
    const rafId = requestAnimationFrame(() => {
      applyVisibility();
    });

    return () => cancelAnimationFrame(rafId);
    // }, [timestampLayerVisibility, timestampLayerSources, viewerRef, timestampLayers, showTimestampLayerBox]);
  }, [timestampLayerVisibility, timestampLayerSources, viewerRef, timestampLayers]);

  // Keep Cesium viewer in sync when helicopterRoutesLayerVisibility is changed programmatically
  useEffect(() => {
    const applyVisibility = () => {
      try {
        const viewer = viewerRef.current?.cesiumElement;
        if (!viewer) return;

        if (viewer.isDestroyed && viewer.isDestroyed()) {
          console.warn('Viewer is destroyed, skipping helicopter routes visibility update');
          return;
        }

        if (!viewer.dataSources || typeof viewer.dataSources.contains !== 'function') {
          console.warn('DataSources collection is not ready, skipping helicopter routes visibility update');
          return;
        }

        for (const layer of helicopterRoutesLayers) {
          const ts = layer.ts;
          const loaded = helicopterRoutesLayerSources[layer.name];

          if (!loaded || !Array.isArray(loaded) || loaded.length === 0) continue;

          const dataSourcesCopy = [...loaded];
          const shouldShow = !!helicopterRoutesLayerVisibility[ts];

          for (const ds of dataSourcesCopy) {
            if (!ds) {
              console.warn('Skipping undefined dataSource for helicopter route', ts);
              continue;
            }

            if (ds.isDestroyed && ds.isDestroyed()) {
              console.warn('Skipping destroyed dataSource for helicopter route', ts);
              continue;
            }

            if (!ds.entities || !ds.name) {
              console.warn('Skipping invalid dataSource (missing entities or name) for helicopter route', ts);
              continue;
            }

            try {
              if (!viewer.dataSources || typeof viewer.dataSources.contains !== 'function') {
                console.warn('DataSources collection became invalid during iteration');
                break;
              }

              const exists = viewer.dataSources.contains(ds);

              if (shouldShow && !exists) {
                if (typeof viewer.dataSources.add === 'function') {
                  viewer.dataSources.add(ds);
                }
              } else if (!shouldShow && exists) {
                try {
                  if (typeof viewer.dataSources.remove === 'function') {
                    viewer.dataSources.remove(ds);
                  }
                } catch (removeError) {
                  console.warn('Failed to remove helicopter route dataSource, may already be removed:', removeError);
                }
              }
            } catch (e) {
              console.warn('Error toggling helicopter route dataSource visibility', e);
            }
          }
        }
      } catch (error) {
        console.warn('Error in applyVisibility for helicopter routes:', error);
      }
    };

    const rafId = requestAnimationFrame(() => {
      applyVisibility();
    });

    return () => cancelAnimationFrame(rafId);
  }, [helicopterRoutesLayerVisibility, helicopterRoutesLayerSources, viewerRef, helicopterRoutesLayers]);

  // Keep Cesium viewer in sync when routesLayerVisibility is changed programmatically
  useEffect(() => {
    const applyVisibility = () => {
      try {
        const viewer = viewerRef.current?.cesiumElement;
        if (!viewer) return;

        if (viewer.isDestroyed && viewer.isDestroyed()) {
          console.warn('Viewer is destroyed, skipping routes visibility update');
          return;
        }

        if (!viewer.dataSources || typeof viewer.dataSources.contains !== 'function') {
          console.warn('DataSources collection is not ready, skipping routes visibility update');
          return;
        }

        for (const layer of routesLayers) {
          const ts = layer.ts;
          const loaded = routesLayerSources[layer.name];

          if (!loaded || !Array.isArray(loaded) || loaded.length === 0) continue;

          const dataSourcesCopy = [...loaded];
          const shouldShow = !!routesLayerVisibility[ts];

          for (const ds of dataSourcesCopy) {
            if (!ds) {
              console.warn('Skipping undefined dataSource for route', ts);
              continue;
            }

            if (ds.isDestroyed && ds.isDestroyed()) {
              console.warn('Skipping destroyed dataSource for route', ts);
              continue;
            }

            if (!ds.entities || !ds.name) {
              console.warn('Skipping invalid dataSource (missing entities or name) for route', ts);
              continue;
            }

            try {
              if (!viewer.dataSources || typeof viewer.dataSources.contains !== 'function') {
                console.warn('DataSources collection became invalid during iteration');
                break;
              }

              const exists = viewer.dataSources.contains(ds);

              if (shouldShow && !exists) {
                if (typeof viewer.dataSources.add === 'function') {
                  viewer.dataSources.add(ds);
                }
              } else if (!shouldShow && exists) {
                try {
                  if (typeof viewer.dataSources.remove === 'function') {
                    viewer.dataSources.remove(ds);
                  }
                } catch (removeError) {
                  console.warn('Failed to remove route dataSource, may already be removed:', removeError);
                }
              }
            } catch (e) {
              console.warn('Error toggling route dataSource visibility', e);
            }
          }
        }
      } catch (error) {
        console.warn('Error in applyVisibility for routes:', error);
      }
    };

    const rafId = requestAnimationFrame(() => {
      applyVisibility();
    });

    return () => cancelAnimationFrame(rafId);
  }, [routesLayerVisibility, routesLayerSources, viewerRef, routesLayers]);

  const toggleHelicopterRoutesLayerVisibility = async (ts, layerName) => {
    await toggleLayerVisibility(ts, layerName, helicopterRoutesLayerVisibility, helicopterRoutesLayerSources, setHelicopterRoutesLayerVisibility);
  }

  async function loadKmlLayer(viewer, layer, layerSources, setLayerSources) {
    if (DEBUG_KML_LOADING) {
      console.debug(`Loading KML layer: ${layer.name}`);
      console.debug("Layer sources before load:", layerSources);
    }

    let files = [];
    for (let i = layer.min_x; i <= layer.max_x; i++) {
      for (let j = layer.min_y; j <= layer.max_y; j++) {
        files.push(`${i}/${j}.kml`);
      }
    }

    let newLayerSource = {};
    newLayerSource[layer.name] = [];
    let loadedCount = 0;
    let failedCount = 0;

    for (const file of files) {
      const url = `${layer.url}/14/${file}`
      if (DEBUG_KML_LOADING) console.debug("Loading:", url);

      try {
        const kmlDataSource = await Cesium.KmlDataSource.load(url, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas,
          clampToGround: true
        });
        newLayerSource[layer.name].push(kmlDataSource);
        loadedCount++;
      } catch (error) {
        failedCount++;
        if (DEBUG_KML_LOADING) console.error("Error loading KML:", file, error);
      }
    }

    await setLayerSources(prev => ({
      ...prev,
      ...newLayerSource,
    }));

    // Simple success/failure message
    if (failedCount === 0) {
      console.log(`KML loaded correctly: ${layer.name} (${loadedCount} tiles)`);
    } else {
      console.warn(`KML load failed: ${layer.name} - ${failedCount} of ${files.length} tiles failed`);
    }

    if (DEBUG_KML_LOADING) {
      console.debug("New layer source:", newLayerSource);
      console.debug("Layer sources after load:", layerSources);
    }
  }

  const loadKmlLayers = async (viewerRef, layers, layerSources, setLayerSources) => {
    if (viewerRef?.current?.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      if (DEBUG_KML_LOADING) console.debug("Loading layers:", layers);

      for (const layer of layers) {
        if (DEBUG_KML_LOADING) console.debug("Processing layer:", layer);
        await loadKmlLayer(viewer, layer, layerSources, setLayerSources);
      }
    } else {
      if (DEBUG_KML_LOADING) console.debug("Viewer not ready to load KML layers...");
    }
  }

  const waitForViewer = (maxTries = 20, delay = 250) =>
    new Promise((resolve, reject) => {
      let tries = 0;
      const interval = setInterval(() => {
        const viewer = viewerRef?.current?.cesiumElement;
        if (viewer) {
          clearInterval(interval);
          resolve(viewer);
        } else if (++tries >= maxTries) {
          clearInterval(interval);
          reject(new Error("Viewer not initialized in time"));
        }
      }, delay);
    });

  const loadKmlLayerButton = async () => {
    // const viewer = viewerRef?.current?.cesiumElement;
    const viewer = await waitForViewer();
    if (viewer) {
      const layer = timestampLayers[0];
      console.log(`Loading layer ${layer.ts}`)
      await loadKmlLayer(viewer, layer, timestampLayerSources, setTimestampLayerSources);
      if (layer.extent) {
        viewer.camera.flyTo({
          destination: Cesium.Rectangle.fromDegrees(layer.extent[0], layer.extent[1], layer.extent[2], layer.extent[3]),
          // destination: layer.extent,
        });
      }
    } else {
      console.debug("viewerRef not ready...");
      setTimeout(() => loadKmlLayerButton(), 250);
      return;
    }
  }

  async function loadGeojsonLayer(viewer, layer, layerSources, setLayerSources) {
    // console.debug(`Inside loadLayer: viewer: ${viewer}; layer: ${layer}`)
    // console.debug("Inside loadGeojsonLayer, layerSources:", layerSources);

    let newLayerSource = {};
    newLayerSource[layer.name] = [];

    try {
      const geoJsonDataSource = await Cesium.GeoJsonDataSource.load(layer.url, {
        camera: viewer.scene.camera,
        canvas: viewer.scene.canvas,
        clampToGround: true,
        stroke: Cesium.Color.ORANGE,
        fill: Cesium.Color.ORANGE,
        strokeWidth: 6,
      });
      // console.debug(`Loaded GeoJson: ${layer.url}`);
      // viewer.dataSources.add(geoJsonDataSource);
      newLayerSource[layer.name].push(geoJsonDataSource);
    } catch (error) {
      console.error("Error loading GeoJson:", layer.url, error);
    }
    setLayerSources(prev => ({
      ...prev,
      ...newLayerSource,
    }));
    // console.debug("End loadGeojsonLayer, layerSources:", layerSources);
    // console.debug("End loadGeojsonLayer, newLayerSource:", newLayerSource);
  }

  const loadGeojsonLayers = async (viewerRef, layers, layerSources, setLayerSources) => {
    if (viewerRef?.current?.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      for (const layer of layers) {
        await loadGeojsonLayer(viewer, layer, layerSources, setLayerSources);
      }
    }
  }

  // const loadRiverWatermask = async () => {
  //   console.debug("River watermask layers:", riverWatermaskLayers);
  //   await loadKmlLayers(viewerRef, riverWatermaskLayers, riverWatermaskLayerSources, setRiverWatermaskLayerSources);
  //   console.debug("River watermask sources:", riverWatermaskLayerSources);
  //   await toggleLayerVisibility(0, "0", riverWatermaskLayerVisibility, riverWatermaskLayerSources, setRiverWatermaskLayerVisibility);
  //   await ensureLayersAtEnd(routesLayerVisibility, routesLayerSources);
  // }
  const loadRiverWatermask = async () => {
    console.debug("River watermask layers:", riverWatermaskLayers);

    // Load the layers and get the sources directly
    const viewer = viewerRef.current.cesiumElement;
    let newSources = {};

    for (const layer of riverWatermaskLayers) {
      let dataSources = [];
      let files = [];

      for (let i = layer.min_x; i <= layer.max_x; i++) {
        for (let j = layer.min_y; j <= layer.max_y; j++) {
          files.push(`${i}/${j}.kml`);
        }
      }

      for (const file of files) {
        const url = `${layer.url}/14/${file}`;
        try {
          const kmlDataSource = await Cesium.KmlDataSource.load(url, {
            camera: viewer.scene.camera,
            canvas: viewer.scene.canvas,
            clampToGround: true
          });
          dataSources.push(kmlDataSource);
          // Add directly to viewer
          viewer.dataSources.add(kmlDataSource);
        } catch (error) {
          console.error("Error loading KML:", file, error);
        }
      }

      newSources[layer.name] = dataSources;
    }

    // Update state for later use
    setRiverWatermaskLayerSources(newSources);

    // Set visibility state to true for all loaded layers
    const visibilityState = {};
    riverWatermaskLayers.forEach(layer => {
      visibilityState[layer.name] = true;
    });
    setRiverWatermaskLayerVisibility(visibilityState);

    await ensureLayersAtEnd(routesLayerVisibility, routesLayerSources);
  }

  const loadLayersAsync = async () => {
    await loadRiverWatermask();
    await loadKmlLayers(viewerRef, timestampLayers, timestampLayerSources, setTimestampLayerSources);
    await loadGeojsonLayers(viewerRef, routesLayers, routesLayerSources, setRoutesLayerSources);
    await loadGeojsonLayers(viewerRef, helicopterRoutesLayers, helicopterRoutesLayerSources, setHelicopterRoutesLayerSources);
  }
  useEffect(() => {
    const dummy = async () => {
      await waitForViewer();
      await loadLayersAsync();
    }
    dummy();
  }, []);

  /* useEffect(() => {
    console.log("viewerRef", viewerRef);
    console.log("viewerRef.current", viewerRef.current);
    console.log("viewerRef.current?.cesiumElement", viewerRef.current?.cesiumElement);
  }, [viewerRef]);

  useEffect(() => {
    const checkViewer = setInterval(() => {
      console.log("check viewer:", !!viewerRef.current?.cesiumElement);
    }, 500);
    return () => clearInterval(checkViewer);
  }, []);

  useEffect(() => {
    console.debug("timesstampLayerSources updated:", timestampLayerSources);
  }, [timestampLayerSources]); */

  // Cloud initialization useEffect
  useEffect(() => {
    if (cloudsEnabled && !cloudsPrimitive) {
      const viewer = viewerRef.current?.cesiumElement;
      if (!viewer) {
        console.debug("Viewer not ready for clouds initialization");
        return;
      }

      console.debug("Initializing volumetric clouds");
      try {
        const { primitive, uniforms, centerPoint } = initializeClouds(viewer, {
          opacity: cloudOpacity,
          threshold: cloudThreshold,
          range: cloudRange,
          steps: cloudSteps,
        });

        viewer.scene.primitives.add(primitive);
        setCloudsPrimitive(primitive);
        setCloudsUniforms(uniforms);
        setCloudsCenterPoint(centerPoint);
        console.debug("Clouds initialized successfully");
      } catch (error) {
        console.error("Error initializing clouds:", error);
      }
    }
  }, [cloudsEnabled, cloudsPrimitive, viewerRef]);

  // Cloud removal useEffect
  useEffect(() => {
    if (!cloudsEnabled && cloudsPrimitive) {
      const viewer = viewerRef.current?.cesiumElement;
      if (viewer && cloudsPrimitive) {
        console.debug("Removing volumetric clouds");
        viewer.scene.primitives.remove(cloudsPrimitive);
        setCloudsPrimitive(null);
        setCloudsUniforms(null);
        setCloudsCenterPoint(null);
      }
    }
  }, [cloudsEnabled, cloudsPrimitive, viewerRef]);

  // Cloud parameter updates useEffect
  useEffect(() => {
    if (cloudsUniforms) {
      console.debug("Updating cloud parameters");
      updateCloudParameters(cloudsUniforms, {
        opacity: cloudOpacity,
        threshold: cloudThreshold,
        range: cloudRange,
        steps: cloudSteps,
      });
    }
  }, [cloudOpacity, cloudThreshold, cloudRange, cloudSteps, cloudsUniforms]);

  // Function to fly to cloud view
  const handleFlyToCloud = () => {
    const viewer = viewerRef.current?.cesiumElement;
    if (viewer && cloudsCenterPoint) {
      flyToCloudView(viewer, cloudsCenterPoint);
    }
  };

  // Rain initialization useEffect
  useEffect(() => {
    if (rainEnabled && !rainStage) {
      const viewer = viewerRef.current?.cesiumElement;
      if (!viewer) {
        console.debug("Viewer not ready for rain initialization");
        return;
      }

      console.debug("Initializing rain effect");
      try {
        const { stage, uniforms } = initializeRainEffect(viewer, {
          intensity: rainIntensity,
          speed: rainSpeed,
          blur: rainBlur,
        });

        setRainStage(stage);
        setRainUniforms(uniforms);
        console.debug("Rain effect initialized successfully");
      } catch (error) {
        console.error("Error initializing rain effect:", error);
      }
    }
  }, [rainEnabled, rainStage, viewerRef]);

  // Rain removal useEffect
  useEffect(() => {
    if (!rainEnabled && rainStage) {
      const viewer = viewerRef.current?.cesiumElement;
      if (viewer && rainStage) {
        console.debug("Removing rain effect");
        cleanupRainEffect(viewer, rainStage);
        setRainStage(null);
        setRainUniforms(null);
      }
    }
  }, [rainEnabled, rainStage, viewerRef]);

  // Rain parameter updates useEffect
  useEffect(() => {
    if (rainStage) {
      console.debug("Updating rain parameters", { rainIntensity, rainSpeed, rainBlur });
      rainStage.uniforms.u_intensity = rainIntensity;
      rainStage.uniforms.u_speed = rainSpeed;
      rainStage.uniforms.u_blur = rainBlur;
    }
  }, [rainIntensity, rainSpeed, rainBlur, rainStage]);

  // Wind initialisation useEffect
  useEffect(() => {
    if (windEnabled && !windPrimitive) {
      const viewer = viewerRef.current?.cesiumElement;
      if (!viewer) {
        console.debug("Viewer not ready for wind initialisation");
        return;
      }

      console.debug("Initialising wind visualisation");
      try {
        const liegeCenter = Cesium.Cartesian3.fromDegrees(5.6185, 50.6332, windAltitude);
        const modelMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(liegeCenter);

        // Calculate wind force in local ENU coordinates
        const dirRad = Cesium.Math.toRadians(windDirection);
        const windForce = new Cesium.Cartesian3(
          Math.cos(dirRad) * windSpeed * 100,  // Scale up for visibility
          Math.sin(dirRad) * windSpeed * 100,
          0.0
        );

        console.log("Wind force:", windForce);

        const ps = new Cesium.ParticleSystem({
          show: true,
          loop: true,
          modelMatrix: modelMatrix,
          minimumSpeed: 0.1,
          maximumSpeed: 1.0,
          lifetime: 12.0,
          emitter: new Cesium.SphereEmitter(8000.0),
          startScale: 1.5,
          endScale: 1.0,
          image: "assets/circular_particle.png",
          emissionRate: windParticleDensity / 5.0,
          startColor: new Cesium.Color(0.9, 0.95, 1.0, 0.7),
          endColor: new Cesium.Color(0.7, 0.8, 0.95, 0.0),
          imageSize: new Cesium.Cartesian2(6, 18),
          forces: [windForce],  // Apply constant wind force to all particles
        });

        const added = viewer.scene.primitives.add(ps);

        // Force render frames to kickstart particle emission
        for (let i = 0; i < 10; i++) {
          viewer.scene.render();
        }

        setWindPrimitive(added);
        setWindCenterPoint(liegeCenter);
        console.debug("Wind visualisation initialised successfully");
      } catch (error) {
        console.error("Error initialising wind visualisation:", error);
      }
    }
  }, [windEnabled, windPrimitive, viewerRef]);

  // Wind removal useEffect
  useEffect(() => {
    if (!windEnabled && windPrimitive) {
      const viewer = viewerRef.current?.cesiumElement;
      if (viewer && windPrimitive) {
        console.debug("Removing wind visualisation");
        viewer.scene.primitives.remove(windPrimitive);
        setWindPrimitive(null);
        setWindData(null);
        setWindCenterPoint(null);
      }
    }
  }, [windEnabled, windPrimitive, viewerRef]);

  // Wind parameter updates useEffect - recreate when sliders change
  useEffect(() => {
    if (windPrimitive && windEnabled) {
      const viewer = viewerRef.current?.cesiumElement;
      if (!viewer) return;

      console.debug("Wind parameters changed, recreating particle system");

      // Remove old particle system
      viewer.scene.primitives.remove(windPrimitive);

      // Create new one with updated parameters
      const liegeCenter = Cesium.Cartesian3.fromDegrees(5.6185, 50.6332, windAltitude);
      const modelMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(liegeCenter);

      const dirRad = Cesium.Math.toRadians(windDirection);
      const windForce = new Cesium.Cartesian3(
        Math.cos(dirRad) * windSpeed * 100,
        Math.sin(dirRad) * windSpeed * 100,
        0.0
      );

      const newWindParticleSystem = viewer.scene.primitives.add(new Cesium.ParticleSystem({
        show: true,
        loop: true,
        modelMatrix: modelMatrix,
        minimumSpeed: 0.1,
        maximumSpeed: 1.0,
        lifetime: 12.0,
        emitter: new Cesium.SphereEmitter(8000.0),
        startScale: 1.5,
        endScale: 1.0,
        image: "assets/circular_particle.png",
        emissionRate: windParticleDensity / 5.0,
        startColor: new Cesium.Color(0.9, 0.95, 1.0, 0.7),
        endColor: new Cesium.Color(0.7, 0.8, 0.95, 0.0),
        imageSize: new Cesium.Cartesian2(6, 18),
        forces: [windForce],
      }));

      // Force renders to kickstart particles
      for (let i = 0; i < 10; i++) {
        viewer.scene.render();
      }

      setWindPrimitive(newWindParticleSystem);
      setWindCenterPoint(liegeCenter);
    }
  }, [windSpeed, windDirection, windParticleDensity, windAltitude, windEnabled, viewerRef]);

  // Function to fly to wind view
  const handleFlyToWind = () => {
    const viewer = viewerRef.current?.cesiumElement;
    if (viewer && windCenterPoint) {
      flyToWindView(viewer, windCenterPoint);
    }
  };

  // GPU Wind initialisation useEffect
  useEffect(() => {
    if (gpuWindEnabled && !gpuWindPrimitive) {
      const viewer = viewerRef.current?.cesiumElement;
      if (!viewer) {
        console.debug("Viewer not ready for GPU wind initialisation");
        return;
      }

      try {
        console.debug("Initialising GPU wind visualisation");
        const result = initialiseGPUWind(viewer, {
          particleCount: gpuWindParticleCount,
          baseSpeed: gpuWindSpeed,
          baseDirection: gpuWindDirection,
          turbulence: gpuWindTurbulence,
          altitude: gpuWindAltitude,
          particleLifetime: gpuWindParticleLifetime,
          particleSize: gpuWindParticleSize,
        });

        setGpuWindPrimitive(result.primitive);
        setGpuWindCenterPoint(result.centerPoint);

        console.debug("GPU wind visualisation initialised successfully");
      } catch (error) {
        console.error("Error initialising GPU wind visualisation:", error);
      }
    }
  }, [gpuWindEnabled, gpuWindPrimitive, viewerRef]);

  // GPU Wind visibility toggle useEffect
  useEffect(() => {
    if (gpuWindPrimitive) {
      gpuWindPrimitive.show = gpuWindEnabled;
      console.debug(`GPU wind visualisation ${gpuWindEnabled ? 'enabled' : 'disabled'}`);
    }
  }, [gpuWindEnabled, gpuWindPrimitive]);

  // GPU Wind parameter updates useEffect
  useEffect(() => {
    if (gpuWindPrimitive && gpuWindEnabled) {
      console.debug("Updating GPU wind parameters");
      gpuWindPrimitive.updateParameters({
        baseSpeed: gpuWindSpeed,
        baseDirection: gpuWindDirection,
        turbulence: gpuWindTurbulence,
        particleLifetime: gpuWindParticleLifetime,
        particleSize: gpuWindParticleSize,
      });
    }
  }, [gpuWindSpeed, gpuWindDirection, gpuWindTurbulence, gpuWindParticleLifetime, gpuWindParticleSize, gpuWindEnabled, gpuWindPrimitive]);

  // GPU Wind particle count changes require recreation
  useEffect(() => {
    if (gpuWindPrimitive && gpuWindEnabled) {
      const viewer = viewerRef.current?.cesiumElement;
      if (!viewer) return;

      console.debug("Recreating GPU wind with new particle count");
      viewer.scene.primitives.remove(gpuWindPrimitive);

      const result = initialiseGPUWind(viewer, {
        particleCount: gpuWindParticleCount,
        baseSpeed: gpuWindSpeed,
        baseDirection: gpuWindDirection,
        turbulence: gpuWindTurbulence,
        altitude: gpuWindAltitude,
        particleLifetime: gpuWindParticleLifetime,
        particleSize: gpuWindParticleSize,
      });

      setGpuWindPrimitive(result.primitive);
      setGpuWindCenterPoint(result.centerPoint);
    }
  }, [gpuWindParticleCount, gpuWindAltitude, gpuWindEnabled, viewerRef]);

  // Function to fly to GPU wind view
  const handleFlyToGpuWind = () => {
    const viewer = viewerRef.current?.cesiumElement;
    if (viewer && gpuWindCenterPoint) {
      flyToGPUWindView(viewer, gpuWindCenterPoint);
    }
  };

  async function flyToBelgium(timestampLayers) {
    if (viewerRef?.current?.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      const layer = timestampLayers[0];
      if (layer.extent) {
        viewer.camera.flyTo({
          destination: Cesium.Rectangle.fromDegrees(layer.extent[0], layer.extent[1], layer.extent[2], layer.extent[3]),
        });
      } else {
        console.log("layer doesn't have extent, so can't flyToBelgium.");
      }
    }
  }

  const flyToBelgiumAsync = async () => {
    await flyToBelgium(timestampLayers);
  }

  // Camera snapshot button handler (top right)
  const handleCameraSnapshot = () => {
    const viewer = viewerRef?.current?.cesiumElement;
    if (!viewer) {
      console.debug('Viewer not ready for snapshot');
      return;
    }
    const c = viewer.camera;
    // Prefer positionCartographic if available (avoid unnecessary conversion)
    const carto = c.positionCartographic || Cesium.Cartographic.fromCartesian(c.position);

    const lat = Cesium.Math.toDegrees(carto.latitude);
    const lon = Cesium.Math.toDegrees(carto.longitude);
    const height = carto.height;

    // heading/pitch/roll are in radians
    const heading = c.heading;
    const pitch = c.pitch;
    const roll = c.roll;

    // Emit a walkthrough JSON snippet (coords are [lat, lon]) with orientation in degrees
    // This is ready to paste into `client/src/services/data/walkthrough.json`.
    const lonF = Number(lon.toFixed(6));
    const latF = Number(lat.toFixed(6));
    const heightF = Number(height.toFixed(2));

    // Convert camera orientation (radians) to degrees for human-friendly JSON
    const headingDeg = Number(Cesium.Math.toDegrees(heading).toFixed(2));
    const pitchDeg = Number(Cesium.Math.toDegrees(pitch).toFixed(2));
    const rollDeg = Number(Cesium.Math.toDegrees(roll).toFixed(2));

    const snippetObj = {
      type: "fly",
      coords: [latF, lonF],
      height: heightF,
      orientationDegrees: {
        heading: headingDeg,
        pitch: pitchDeg,
        roll: rollDeg
      }
    };

    const snippetText = JSON.stringify(snippetObj, null, 2);
    console.log('Walkthrough snippet (paste into walkthrough.json):');
    console.log(snippetText);
    console.log('Current walkthrough step:', currentWalkthroughStep);

    const currentWalkthroughStepJSON = currentWalkthroughStep
      ? JSON.stringify(currentWalkthroughStep, null, 2)
      : 'No walkthrough step active';

    const combinedText = currentWalkthroughStepJSON + "\n\n" + snippetText;

    alert(combinedText);

    // Copy combined JSON to clipboard if available
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(combinedText).then(() => {
          console.debug('Walkthrough JSON copied to clipboard');
        }).catch(() => { });
      }
    } catch (e) {
      // ignore clipboard failures
    }
  }

  return (
    <>
      <div className="cesium-control-group">
        <div className="custom-cesium-controls">
          <button><img src={CustomControlLineIcon} /></button>
          <button><img src={CustomControlPolygonIcon} /></button>
          <button
            onMouseDown={handleZoomInStart}
            onMouseUp={handleZoomInEnd}
            onMouseLeave={handleZoomInEnd}
          >
            <img src={CustomControlZoomIn} />
          </button>
          <button
            onMouseDown={handleZoomOutStart}
            onMouseUp={handleZoomOutEnd}
            onMouseLeave={handleZoomOutEnd}
          >
            <img src={CustomControlZoomOut} />
          </button>
          <button><img src={CustomControlGlobe} /></button>
          {/* <button onClick={() => {
              setShowLayerBox(!showLayerBox);
              setShowBubble(true);
              setShowFolder(false);
              setShowCloudsBox(false);
              setShowRainBox(false);
              setShowWindBox(false);
              setShowGpuWindBox(false);
              setShowBubble(false);
              setShowFolder(false);
            }}>
              <img src={CustomControlLayers} />
              <img src={CustomControlLayers} />
            </button> */}
          <button
            title="Snapshot camera (copy walkthrough JSON)"
            onClick={handleCameraSnapshot}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 14C3.06812 14 2.60218 14 2.23463 13.8478C1.74458 13.6448 1.35523 13.2554 1.15224 12.7654C1 12.3978 1 11.9319 1 11V4.2C1 3.0799 1 2.51984 1.21799 2.09202C1.40973 1.71569 1.71569 1.40973 2.09202 1.21799C2.51984 1 3.0799 1 4.2 1H11C11.9319 1 12.3978 1 12.7654 1.15224C13.2554 1.35523 13.6448 1.74458 13.8478 2.23463C14 2.60218 14 3.06812 14 4M11.2 21H17.8C18.9201 21 19.4802 21 19.908 20.782C20.2843 20.5903 20.5903 20.2843 20.782 19.908C21 19.4802 21 18.9201 21 17.8V11.2C21 10.0799 21 9.51984 20.782 9.09202C20.5903 8.71569 20.2843 8.40973 19.908 8.21799C19.4802 8 18.9201 8 17.8 8H11.2C10.0799 8 9.51984 8 9.09202 8.21799C8.71569 8.40973 8.40973 8.71569 8.21799 9.09202C8 9.51984 8 10.0799 8 11.2V17.8C8 18.9201 8 19.4802 8.21799 19.908C8.40973 20.2843 8.71569 20.5903 9.09202 20.782C9.51984 21 10.0799 21 11.2 21Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>coords</span>
          </button>
        </div>

      </div>

      <div className="cesium-control-group-rhs">
        <div className="custom-cesium-controls-rhs">

          {showLayerBox && (
            <div className="layer-control-box">
              <LayerBox
                viewerRef={viewerRef}
                layerSources={layerSources}
                formattedLayers={formattedLayers}
                loadProject={loadProject}
                layerProgress={layerProgress}
              />
            </div>
          )}
          {/* <button onClick={() => setShowLayerTimestampSelector(!showLayerTimestampSelector)}>
              <img src={CustomControlLayers} />
              <span>Flood/Route Selector</span>
            </button>
            {showLayerTimestampSelector && (
              <LayerTimestampSelector
                timestamps={timestampLayers || []}
                selectedTimestamp={selectedTimestamp}
                setSelectedTimestamp={setSelectedTimestamp}
                selectedLayerType={selectedLayerType}
                setSelectedLayerType={setSelectedLayerType}
                viewerRef={viewerRef}
                name={"Flood Timestamps"}
                layers={timestampLayers}
                layerVisibility={timestampLayerVisibility}
                toggleLayerVisibility={toggleTimestampLayerVisibility}
              />
            )} */}
          {showFloodTimestamps && (
            <div className='slider-container'>
              <button onClick={() => {
                setShowLayerBox(false);
                setShowTimestampLayerBox(!showTimestampLayerBox);
                setShowRoutesLayerBox(false);
                setShowHelicopterRoutesLayerBox(false);
                setShowCloudsBox(false);
                setShowRainBox(false);
                setShowWindBox(false);
                setShowGpuWindBox(false);
                setShowBubble(false);
                setShowFolder(false);
              }}>
                <img src={CustomControlRainDropIcon} />
              </button>
              {showTimestampLayerBox && (
                <NewLayerBox
                  viewerRef={viewerRef}
                  name={"Flood Timestamps"}
                  layers={timestampLayers}
                  layerVisibility={timestampLayerVisibility}
                  toggleLayerVisibility={toggleTimestampLayerVisibility}
                  currentIndex={currentTimestampLayerIndex}
                  stopAnimation={stopTimestampAnimation}
                />
              )}
            </div>
          )}
          {showTallBuildings && (
            <div className='slider-container'>
              <button onClick={() => {
                setShowLayerBox(false);
                setShowTimestampLayerBox(false);
                setShowRoutesLayerBox(!showRoutesLayerBox);
                setShowHelicopterRoutesLayerBox(false);
                setShowCloudsBox(false);
                setShowRainBox(false);
                setShowWindBox(false);
                setShowGpuWindBox(false);
                setShowBubble(false);
                setShowFolder(false);
              }}>
                <img src={CustomControlLocationPinIcon} />
              </button>
              {showRoutesLayerBox && (
                <NewLayerBox
                  viewerRef={viewerRef}
                  name={"Routes"}
                  layers={routesLayers}
                  layerVisibility={routesLayerVisibility}
                  toggleLayerVisibility={toggleRoutesLayerVisibility}
                />
              )}
            </div>
          )}
          {showHelicopterEvacRoutes && (
            <div className='slider-container'>
              <button onClick={() => {
                setShowLayerBox(false);
                setShowTimestampLayerBox(false);
                setShowRoutesLayerBox(false);
                setShowHelicopterRoutesLayerBox(!showHelicopterRoutesLayerBox);
                setShowCloudsBox(false);
                setShowRainBox(false);
                setShowWindBox(false);
                setShowGpuWindBox(false);
                setShowBubble(false);
                setShowFolder(false);
              }}>
                <img src={CustomControlHelicopterIcon} />
              </button>
              {showHelicopterRoutesLayerBox && (
                <NewLayerBox
                  viewerRef={viewerRef}
                  name={"Helicopter Evac Routes"}
                  layers={helicopterRoutesLayers}
                  layerVisibility={helicopterRoutesLayerVisibility}
                  toggleLayerVisibility={toggleHelicopterRoutesLayerVisibility}
                  currentIndex={currentHelicopterRoutesLayerIndex}
                />
              )}
            </div>
          )}
          {/* <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowCloudsBox(!showCloudsBox);
              setShowRainBox(false);
              setShowWindBox(false);
              setShowGpuWindBox(false);
              setShowBubble(false);
              setShowFolder(false);
            }}>
                <img src={CustomControlCloudsIcon} />
            </button>
            {showCloudsBox && (
              <div className="layer-control-box">
                <CloudControlBox
                  cloudsEnabled={cloudsEnabled}
                  setCloudsEnabled={setCloudsEnabled}
                  cloudOpacity={cloudOpacity}
                  setCloudOpacity={setCloudOpacity}
                  cloudThreshold={cloudThreshold}
                  setCloudThreshold={setCloudThreshold}
                  cloudRange={cloudRange}
                  setCloudRange={setCloudRange}
                  cloudSteps={cloudSteps}
                  setCloudSteps={setCloudSteps}
                  onFlyToCloud={handleFlyToCloud}
                />
              </div>
            )}
            <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowHelicopterRoutesLayerBox(false);
              setShowCloudsBox(false);
              setShowRainBox(!showRainBox);
              setShowWindBox(false);
              setShowGpuWindBox(false);
              setShowBubble(false);
              setShowFolder(false);
            }}>
                <img src={CustomControlRainIcon} />
            </button>
            {showRainBox && (
              <div className="layer-control-box">
                <RainControlBox
                  rainEnabled={rainEnabled}
                  setRainEnabled={setRainEnabled}
                  rainIntensity={rainIntensity}
                  setRainIntensity={setRainIntensity}
                  rainSpeed={rainSpeed}
                  setRainSpeed={setRainSpeed}
                  rainBlur={rainBlur}
                  setRainBlur={setRainBlur}
                />
              </div>
            )}
            <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowHelicopterRoutesLayerBox(false);
              setShowCloudsBox(false);
              setShowRainBox(false);
              setShowWindBox(!showWindBox);
              setShowBubble(false);
              setShowFolder(false);
            }}>
                <img src={CustomControlWindIcon} />
            </button>
            {showWindBox && (
              <div className="layer-control-box">
                <WindControlBox
                  windEnabled={windEnabled}
                  setWindEnabled={setWindEnabled}
                  windSpeed={windSpeed}
                  setWindSpeed={setWindSpeed}
                  windDirection={windDirection}
                  setWindDirection={setWindDirection}
                  windParticleDensity={windParticleDensity}
                  setWindParticleDensity={setWindParticleDensity}
                  windAltitude={windAltitude}
                  setWindAltitude={setWindAltitude}
                  windTurbulence={windTurbulence}
                  setWindTurbulence={setWindTurbulence}
                  onFlyToWind={handleFlyToWind}
                />
              </div>
            )}
            <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowHelicopterRoutesLayerBox(false);
              setShowCloudsBox(false);
              setShowRainBox(false);
              setShowWindBox(false);
              setShowGpuWindBox(!showGpuWindBox);
              setShowBubble(false);
              setShowFolder(false);
            }}>
                <img src={CustomControlGPUWindIcon} />
            </button>
            {showGpuWindBox && (
              <div className="layer-control-box">
                <GPUWindControlBox
                  gpuWindEnabled={gpuWindEnabled}
                  setGpuWindEnabled={setGpuWindEnabled}
                  gpuWindSpeed={gpuWindSpeed}
                  setGpuWindSpeed={setGpuWindSpeed}
                  gpuWindDirection={gpuWindDirection}
                  setGpuWindDirection={setGpuWindDirection}
                  gpuWindParticleCount={gpuWindParticleCount}
                  setGpuWindParticleCount={setGpuWindParticleCount}
                  gpuWindAltitude={gpuWindAltitude}
                  setGpuWindAltitude={setGpuWindAltitude}
                  gpuWindTurbulence={gpuWindTurbulence}
                  setGpuWindTurbulence={setGpuWindTurbulence}
                  gpuWindParticleLifetime={gpuWindParticleLifetime}
                  setGpuWindParticleLifetime={setGpuWindParticleLifetime}
                  gpuWindParticleSize={gpuWindParticleSize}
                  setGpuWindParticleSize={setGpuWindParticleSize}
                  onFlyToGpuWind={handleFlyToGpuWind}
                />
              </div>
            )} */}
          {showTallBuildings && (
            <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowHelicopterRoutesLayerBox(false);
              setShowCloudsBox(false);
              setShowRainBox(false);
              setShowWindBox(false);
              setShowGpuWindBox(false);
              setShowBubble(false);
              setShowFolder(false);
              toggleOSMBuildingVisibility();
            }}>
              <img src={CustomControlBuildingIcon} />
            </button>
          )}
          {/* <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowHelicopterRoutesLayerBox(false);
              setShowBubble(false);
              setShowFolder(false);
              // loadHardcodedTiles();
              loadKmlLayerButton();
            }}>
                <img src={CustomControlGlobe} />
            </button> */}
          {/* <button onClick={() => {
              setShowLayerBox(false);
              setShowTimestampLayerBox(false);
              setShowRoutesLayerBox(false);
              setShowHelicopterRoutesLayerBox(false);
              setShowCloudsBox(false);
              setShowRainBox(false);
              setShowWindBox(false);
              setShowGpuWindBox(false);
              setShowBubble(false);
              setShowFolder(false);
              flyToBelgiumAsync();
            }}>
                <img src={CustomControlZoomIn} />
            </button> */}
        </div>
      </div>
    </>
  );
});

export default CesiumControls;